import React, { Suspense, useEffect } from "react";
import HeadingLayout from "src/components/base/heading-layout";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify/unstyled";
import { Button } from "src/components/base/button";
import { Subheading } from "src/components/base/heading";
import { Strong, Text, TextError } from "src/components/base/text";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Divider } from "src/components/base/divider";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { EllipsisVerticalIcon, FingerPrintIcon, KeyIcon } from "@heroicons/react/20/solid";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "src/components/base/dropdown";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { ApiPublic } from "src/api/api_public";
import { StartPostAuthResetResponse } from "src/api/generated/frontend_public_v1";
import { ERROR_STORE } from "src/context/error-context";
import AddWebauthKeyDialog from "src/components/dialogs/add-webauth-key";
import CreatePasswordDialog from "src/components/dialogs/create-password";
import { Badge } from "src/components/base/badge";
import AddNewTotpKeyDialog from "src/components/dialogs/add-new-totp";

/**
 * The properties for {@link ResetCredentials}
 */
export type ResetCredentialsProps = {
    /** The function that should be executed on a successful reset */
    onSubmit: () => void;
};

/**
 * The form to reset the credentials of the user used after logging in, for the post-auth reset form
 */
export default function ResetCredentials(props: ResetCredentialsProps) {
    const [t] = useTranslation("reset");
    const [tg] = useTranslation();

    const [resetCredentials, setResetCredentials] = React.useState<StartPostAuthResetResponse>();

    // Dialogs
    const [openTOTP, setOpenTOTP] = React.useState(false);
    const [openPW, setOpenPW] = React.useState(false);
    const [confirmSubmit, setConfirmSubmit] = React.useState(false);
    const [openWebAuth, setOpenWebAuth] = React.useState(false);

    // Errors
    const [missingWebAuthnError, setMissingWebAuthnError] = React.useState<string | undefined>(undefined);
    const [removePasswordError, setRemovePasswordError] = React.useState<string | undefined>(undefined);
    const [missingPasswordError, setMissingPasswordError] = React.useState<string | undefined>(undefined);
    const [missingTotpError, setMissingTotpError] = React.useState<string | undefined>(undefined);

    /**
     * Load the current state of the credentials
     */
    const loadResetCredentials = async () => {
        setResetCredentials(await ApiPublic.credentials.startPostAuthReset());
    };

    /**
     * Delete the password
     */
    const deletePassword = async () => {
        if (resetCredentials?.webauthn.length === 0) {
            setRemovePasswordError(t("error.cannot-delete-password-without-webauth-key"));
            return;
        }
        const res = await ApiPublic.credentials.deletePassword();

        if (res.result === "Err") {
            if (res.error.only_login_method) {
                toast.error(t("toast.one-login-necessary"));
            }
        } else {
            toast.success(t("toast.removed-password"));
        }
        setRemovePasswordError(undefined);
        await loadResetCredentials();
    };

    /**
     * Deletes an TOTP App
     *
     * @param uuid The uuid of the app
     */
    const deleteTotpApp = async (uuid: string) => {
        await ApiPublic.credentials.deleteTotpKey(uuid);
        toast.success(t("toast.removed-totp-app"));
        await loadResetCredentials();
    };

    /**
     * Delete a webauthn key
     *
     * @param uuid The uuid of the key
     */
    const deleteWebauthnKey = async (uuid: string) => {
        const res = await ApiPublic.credentials.deleteWebauthnKey(uuid);

        if (res.result === "Ok") {
            toast.success(t("toast.removed-security-key"));
        } else {
            if (res.error.only_login_method) {
                toast.error(t("toast.one-login-necessary"));
            }
        }
        await loadResetCredentials();
    };

    /**
     * Provides the translation for which level of requirements are required
     *
     * @returns translation for requirements
     */
    const getTextForRequirement = () => {
        switch (resetCredentials?.requirements) {
            // No requirements mean no translation
            case "None":
                return "";
            case "MultiFactor":
                return t("description.requirement-multifactor");
            case "Passwordless":
                return t("description.requirement-passwordless");
            case undefined:
            default:
                return "";
        }
    };

    /**
     * Check if the credential policies are enforced, bubbling a toast notification
     * and return whether it is fulfilled (requires an up-to-date `resetCredentials`)
     *
     * @returns whether the policy is fulfilled
     */
    const checkPolicyEnforcement = () => {
        let result = true;
        if (resetCredentials?.requirements === "Passwordless") {
            if (resetCredentials?.webauthn.length === 0) {
                setMissingWebAuthnError(t("error.webauthn-required"));
                result = false;
            }
            if (resetCredentials?.password || resetCredentials?.totp.length > 0) {
                setRemovePasswordError(t("error.remove-password"));
                result = false;
            }
        } else if (resetCredentials?.requirements === "MultiFactor") {
            if (
                (resetCredentials.password === undefined || !resetCredentials?.password) &&
                resetCredentials?.webauthn.length === 0
            ) {
                setMissingPasswordError(t("error.need-password"));
                result = false;
            }
            if (resetCredentials?.password && resetCredentials?.totp.length === 0) {
                setMissingTotpError(t("error.missing-totp-for-mfa"));
                result = false;
            }
        }
        return result;
    };

    /**
     * Check if the credential policy is enforced, then open the submit confirmation dialog
     */
    const tryConfirmAndSubmit = async () => {
        await loadResetCredentials();
        if (checkPolicyEnforcement()) {
            setConfirmSubmit(true);
        }
    };

    /**
     * Submit the reset credentials form
     */
    const submit = async () => {
        const res = await ApiPublic.credentials.submit();

        if (res.result === "Err") {
            if (res.error.invitation_expired) {
                ERROR_STORE.report(new Error("This should not happen"));
            }
            if (res.error.missing_primary_factor) {
                toast.error(t("toast.missing-primary-factor"));
            }
            return;
        }

        props.onSubmit();
    };

    useEffect(() => {
        loadResetCredentials().then();
    }, []);

    if (!resetCredentials) {
        return (
            <div className={"flex h-screen w-full items-center justify-center p-10"}>
                <div className={"flex flex-col items-center gap-6"}>
                    <Subheading>{t("heading.invalid-link")}</Subheading>
                    <Button href={"/"}>{t("button.back-to-login")}</Button>
                </div>
            </div>
        );
    }

    return (
        <div className={"flex w-full items-center justify-center"}>
            <div
                className={
                    "mt-8 mb-8 flex justify-center rounded-xl border bg-white p-10 dark:border-zinc-800 dark:bg-zinc-900 dark:before:pointer-events-none forced-colors:outline"
                }
            >
                <HeadingLayout
                    className={"w-full gap-6"}
                    heading={t("heading.top")}
                    headingDescription={t("description.top") + "\n" + getTextForRequirement()}
                    headingChildren={
                        <Button
                            color={"blue"}
                            onClick={async () => {
                                await tryConfirmAndSubmit();
                            }}
                        >
                            {t("button.save")}
                        </Button>
                    }
                >
                    <div className={"flex flex-col gap-4"}>
                        <div className={"flex items-center gap-4"}>
                            <Text>
                                <FingerPrintIcon className={"size-6"} />
                            </Text>
                            <div>
                                <Subheading>{t("heading.password-less")}</Subheading>
                                <Badge
                                    color={resetCredentials.webauthn.length === 0 ? "zinc" : "blue"}
                                    className={"w-fit"}
                                >
                                    {resetCredentials.webauthn.length === 0
                                        ? t("label.not-configured")
                                        : t("label.configured")}
                                </Badge>
                            </div>
                        </div>
                        <Text>{t("description.password-less")}</Text>
                        {missingWebAuthnError && <TextError>{missingWebAuthnError}</TextError>}
                        <div className={"flex flex-col gap-3"}>
                            <div className={"flex flex-col justify-between md:flex-row"}>
                                <Subheading className={"mt-6"}>{t("heading.existing-security-keys")}</Subheading>
                                <Button outline={true} className={"w-fit"} onClick={() => setOpenWebAuth(true)}>
                                    {t("button.create-security-key")}
                                </Button>
                            </div>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{t("label.name")}</TableHeader>
                                        <TableHeader className={"w-0"}>
                                            <span className={"sr-only"}>{t("label.actions")}</span>
                                        </TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {resetCredentials.webauthn.map((pk) => (
                                        <TableRow key={pk.uuid}>
                                            <TableCell>{pk.label}</TableCell>
                                            <TableCell>
                                                <Dropdown>
                                                    <DropdownButton plain={true}>
                                                        <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                                                        <EllipsisVerticalIcon />
                                                    </DropdownButton>

                                                    <DropdownMenu anchor={"bottom end"}>
                                                        <DropdownItem
                                                            onClick={() =>
                                                                deleteWebauthnKey(pk.uuid).then(loadResetCredentials)
                                                            }
                                                        >
                                                            {t("button.delete")}
                                                        </DropdownItem>
                                                    </DropdownMenu>
                                                </Dropdown>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    <div className={"flex flex-col gap-4"}>
                        <div className={"flex flex-col justify-between gap-4 md:flex-row"}>
                            <div className={"flex items-center gap-4"}>
                                <Text>
                                    <KeyIcon className={"size-6"} />
                                </Text>
                                <Strong className={"flex flex-col gap-1"}>
                                    {t("label.password")}
                                    <Badge color={resetCredentials.password ? "blue" : "zinc"} className={"w-fit"}>
                                        {resetCredentials.password ? t("label.configured") : t("label.not-configured")}
                                    </Badge>
                                </Strong>
                            </div>
                            <div className={"flex items-center gap-2"}>
                                {resetCredentials.requirements !== "Passwordless" && (
                                    <Button outline={true} onClick={() => setOpenPW(true)}>
                                        {resetCredentials.password
                                            ? t("button.update-password")
                                            : t("button.set-password")}
                                    </Button>
                                )}
                                <Button
                                    onClick={async () => {
                                        await deletePassword();
                                        await loadResetCredentials();
                                    }}
                                    disabled={!resetCredentials.password}
                                    outline={true}
                                >
                                    {t("button.remove-password")}
                                </Button>
                            </div>
                        </div>

                        {removePasswordError && <TextError>{removePasswordError}</TextError>}
                        {missingPasswordError && <TextError>{missingPasswordError}</TextError>}
                    </div>

                    {resetCredentials.password && (
                        <>
                            <Divider soft={true} />
                            <div className={"flex flex-col gap-3"}>
                                <div className={"flex flex-col justify-between gap-1"}>
                                    <Subheading>{t("heading.mfa")}</Subheading>
                                    <Badge
                                        color={resetCredentials.totp.length === 0 ? "zinc" : "blue"}
                                        className={"w-fit"}
                                    >
                                        {resetCredentials.totp.length === 0
                                            ? t("label.not-configured")
                                            : t("label.configured")}
                                    </Badge>
                                </div>
                                {missingTotpError && <TextError>{missingTotpError}</TextError>}
                                <div className={"flex flex-col justify-between md:flex-row"}>
                                    <Subheading>{t("heading.existing-totp")}</Subheading>
                                    <Button outline={true} onClick={() => setOpenTOTP(true)}>
                                        {t("button.create-totp")}
                                    </Button>
                                </div>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>{t("label.label")}</TableHeader>
                                            <TableHeader className={"w-0"}>
                                                <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                                            </TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {resetCredentials.totp.map((totp) => (
                                            <TableRow key={totp.uuid}>
                                                <TableCell>{totp.label}</TableCell>
                                                <TableCell>
                                                    <Dropdown>
                                                        <DropdownButton plain={true}>
                                                            <span className={"sr-only"}>
                                                                {tg("accessibility.actions")}
                                                            </span>
                                                            <EllipsisVerticalIcon />
                                                        </DropdownButton>
                                                        <DropdownMenu anchor={"bottom end"}>
                                                            <DropdownItem
                                                                onClick={() =>
                                                                    deleteTotpApp(totp.uuid).then(loadResetCredentials)
                                                                }
                                                            >
                                                                <DropdownLabel>{t("button.delete")}</DropdownLabel>
                                                            </DropdownItem>
                                                        </DropdownMenu>
                                                    </Dropdown>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}
                </HeadingLayout>

                <Suspense>
                    <AddNewTotpKeyDialog
                        isOpen={openTOTP}
                        endpoint={"public"}
                        onClose={() => setOpenTOTP(false)}
                        onSubmit={async () => {
                            toast.success(t("toast.added-totp-app"));
                            setOpenTOTP(false);
                            setMissingTotpError(undefined);
                            await loadResetCredentials();
                        }}
                        mail={resetCredentials.mail}
                    />

                    <AddWebauthKeyDialog
                        onClose={async () => {
                            await loadResetCredentials();
                            setOpenWebAuth(false);
                            setMissingWebAuthnError(undefined);
                        }}
                        type={"invitation"}
                        isOpen={openWebAuth}
                    />

                    <CreatePasswordDialog
                        isOpen={openPW}
                        endpoint={"public"}
                        onClose={() => setOpenPW(false)}
                        onSubmit={async () => {
                            await loadResetCredentials();
                            toast.success(t("toast.set-password"));
                            setOpenPW(false);
                            setMissingPasswordError(undefined);
                        }}
                    />
                </Suspense>

                {confirmSubmit && (
                    <Dialog open={confirmSubmit} onClose={() => setConfirmSubmit(false)}>
                        <DialogTitle>{t("heading.confirm-submit")}</DialogTitle>
                        <DialogDescription>{t("description.confirm-submit")}</DialogDescription>
                        <DialogActions>
                            <Button onClick={submit}>{t("button.confirm")}</Button>
                        </DialogActions>
                    </Dialog>
                )}
            </div>
            <BlockUnsavedChanges condition={!confirmSubmit} />
        </div>
    );
}
